/**
 * Scenario Setup — Preset selection + editable tables + asset builder form
 */

import { useState } from 'react'
import type { AARScenarioConfig, AARReceiver, AARTanker, ReceiverPriority } from '../../../../types'
import { getScenarioPresets, ASSET_DEFAULTS } from '../../../../services/aarEngine'

interface ScenarioSetupProps {
  onStart: (config: AARScenarioConfig) => void
}

const ASSET_TYPES = Object.keys(ASSET_DEFAULTS)

export function ScenarioSetup({ onStart }: ScenarioSetupProps) {
  const presets = getScenarioPresets()
  const [selectedPreset, setSelectedPreset] = useState<string | null>(null)
  const [config, setConfig] = useState<AARScenarioConfig | null>(null)

  // Asset builder state
  const [builderType, setBuilderType] = useState(ASSET_TYPES[0])
  const [builderCallsign, setBuilderCallsign] = useState('')
  const [builderFlightSize, setBuilderFlightSize] = useState(1)
  const [builderFuelReq, setBuilderFuelReq] = useState(0)
  const [builderBurnRate, setBuilderBurnRate] = useState(0)
  const [builderPriority, setBuilderPriority] = useState<ReceiverPriority>('MEDIUM')
  const [builderME, setBuilderME] = useState(false)
  const [builderArctStart, setBuilderArctStart] = useState('0030')
  const [builderArctEnd, setBuilderArctEnd] = useState('0100')
  const [builderOffload, setBuilderOffload] = useState(120000)

  const handlePresetSelect = (presetId: string) => {
    const preset = presets.find(p => p.id === presetId)
    if (!preset) return
    setSelectedPreset(presetId)
    setConfig(JSON.parse(JSON.stringify(preset.config)))
  }

  const handleStart = () => {
    if (!config) return
    if (config.tankers.length === 0 || config.receivers.length === 0) return
    onStart(config)
  }

  const updateReceiver = (idx: number, field: keyof AARReceiver, value: string | number | boolean) => {
    if (!config) return
    const receivers = [...config.receivers]
    receivers[idx] = { ...receivers[idx], [field]: value }
    setConfig({ ...config, receivers })
  }

  const updateTanker = (idx: number, field: keyof AARTanker, value: string | number) => {
    if (!config) return
    const tankers = [...config.tankers]
    tankers[idx] = { ...tankers[idx], [field]: value }
    setConfig({ ...config, tankers })
  }

  const deleteReceiver = (idx: number) => {
    if (!config) return
    const receivers = config.receivers.filter((_, i) => i !== idx)
    setConfig({ ...config, receivers })
  }

  const deleteTanker = (idx: number) => {
    if (!config) return
    const tankers = config.tankers.filter((_, i) => i !== idx)
    setConfig({ ...config, tankers })
  }

  // Update builder defaults when asset type changes
  const handleBuilderTypeChange = (type: string) => {
    setBuilderType(type)
    const defaults = ASSET_DEFAULTS[type]
    if (defaults) {
      setBuilderBurnRate(defaults.burnRate)
      setBuilderFuelReq(defaults.role === 'receiver' ? Math.round(defaults.fuelCapacity * 0.8) : 0)
      setBuilderOffload(defaults.defaultOffload ?? 120000)
    }
  }

  const zuluToMinutes = (zulu: string): number => {
    const h = parseInt(zulu.slice(0, 2), 10) || 0
    const m = parseInt(zulu.slice(2, 4), 10) || 0
    return h * 60 + m
  }

  const handleAddAsset = () => {
    if (!config || !builderCallsign.trim()) return
    const defaults = ASSET_DEFAULTS[builderType]
    if (!defaults) return
    const id = builderCallsign.trim().replace(/\s+/g, '-').toUpperCase()
    const startZuluMin = zuluToMinutes(config.startTimeZulu)
    const arctStartMin = zuluToMinutes(builderArctStart) - startZuluMin
    const arctEndMin = zuluToMinutes(builderArctEnd) - startZuluMin

    if (defaults.role === 'tanker') {
      const newTanker: AARTanker = {
        id,
        callsign: builderCallsign.trim().toUpperCase(),
        assetType: builderType,
        totalOffload: builderOffload,
        offloaded: 0,
        flowRate: defaults.flowRate ?? 900,
        arTrack: config.arTracks[0]?.name ?? 'AR-100',
        availableWindow: [Math.max(0, arctStartMin), Math.max(0, arctEndMin)],
        status: 'AVAILABLE',
        queuedReceivers: [],
        currentReceiver: null,
      }
      setConfig({ ...config, tankers: [...config.tankers, newTanker] })
    } else {
      const newReceiver: AARReceiver = {
        id,
        callsign: builderCallsign.trim().toUpperCase(),
        assetType: builderType,
        fuelCapacity: defaults.fuelCapacity,
        currentFuel: Math.round(defaults.fuelCapacity * 0.6),
        burnRate: builderBurnRate * builderFlightSize,
        bingoFuel: defaults.bingoFuel,
        jokerFuel: Math.round(defaults.bingoFuel * 1.3),
        requestedOffload: builderFuelReq * builderFlightSize,
        flightSize: builderFlightSize,
        priority: builderPriority,
        missionEssential: builderME,
        arctWindow: [Math.max(0, arctStartMin), Math.max(0, arctEndMin)],
        status: 'WAITING',
      }
      setConfig({ ...config, receivers: [...config.receivers, newReceiver] })
    }
    setBuilderCallsign('')
  }

  // Init empty config for "Build Your Own"
  const handleInitCustom = () => {
    setSelectedPreset(null)
    setConfig({
      name: 'CUSTOM SCENARIO',
      timeWindowMin: 120,
      startTimeZulu: '0000',
      arTracks: [{ id: 'AR-100', name: 'AR-100 ALPHA', altitude: 'FL250-FL280', tankerIds: [] }],
      tankers: [],
      receivers: [],
    })
  }

  const isTankerType = ASSET_DEFAULTS[builderType]?.role === 'tanker'

  return (
    <div className="flex flex-col gap-4 flex-1 min-h-0">
      {/* Preset Cards */}
      <div className="grid grid-cols-3 gap-3">
        {presets.map(preset => {
          const isSelected = selectedPreset === preset.id
          return (
            <button
              key={preset.id}
              onClick={() => handlePresetSelect(preset.id)}
              className={`text-left p-3 rounded border transition-colors ${
                isSelected
                  ? 'border-term-primary bg-term-primary/10 ring-1 ring-term-primary'
                  : 'border-term-border/50 bg-term-bgDark/50 hover:bg-term-bgLight hover:border-term-primary/40'
              }`}
            >
              <div className="text-xs font-bold text-term-primary tracking-wider">{preset.name}</div>
              <div className="text-[10px] text-term-textDim mt-1 leading-relaxed">{preset.description}</div>
              <div className="flex gap-3 mt-2 text-[10px] text-term-textDim">
                <span>{preset.config.tankers.length} tanker{preset.config.tankers.length > 1 ? 's' : ''}</span>
                <span>{preset.config.receivers.length} receiver{preset.config.receivers.length > 1 ? 's' : ''}</span>
                <span>{preset.config.timeWindowMin} min</span>
              </div>
            </button>
          )
        })}
      </div>

      {/* Build Your Own button */}
      {!config && (
        <div className="flex flex-col items-center gap-2 py-4">
          <div className="text-term-textDim text-sm">Select a scenario preset above, or</div>
          <button
            onClick={handleInitCustom}
            className="term-button term-button-primary px-4 py-2 text-xs font-bold uppercase tracking-wider"
          >
            BUILD YOUR OWN
          </button>
        </div>
      )}

      {config && (
        <div className="flex flex-col gap-3 flex-1 min-h-0 overflow-auto">
          {/* Scenario Name + Zulu Time */}
          <div className="term-box p-3 flex items-center gap-4">
            <div className="flex-1">
              <div className="text-xs text-term-textDim font-medium uppercase tracking-wider mb-1">Scenario</div>
              <div className="text-sm font-bold text-term-text">{config.name}</div>
            </div>
            <div>
              <label className="text-[9px] text-term-textDim uppercase block mb-0.5">Mission Start (Zulu)</label>
              <input
                type="text"
                value={config.startTimeZulu}
                onChange={e => setConfig({ ...config, startTimeZulu: e.target.value.replace(/[^0-9]/g, '').slice(0, 4) })}
                placeholder="0000"
                maxLength={4}
                className="term-input py-1 px-2 w-20 text-xs font-mono tabular-nums text-center"
              />
            </div>
            <div>
              <label className="text-[9px] text-term-textDim uppercase block mb-0.5">Window (min)</label>
              <input
                type="number"
                value={config.timeWindowMin}
                onChange={e => setConfig({ ...config, timeWindowMin: parseInt(e.target.value) || 60 })}
                className="term-input py-1 px-2 w-20 text-xs tabular-nums text-center"
              />
            </div>
          </div>

          {/* Tankers Table */}
          <div className="term-box p-3">
            <div className="text-xs text-term-textDim font-medium uppercase tracking-wider mb-2">Tankers</div>
            {config.tankers.length === 0 ? (
              <div className="text-[10px] text-term-textDim italic py-2">No tankers — add one below</div>
            ) : (
              <table className="term-table">
                <thead>
                  <tr>
                    <th>Callsign</th>
                    <th>Type</th>
                    <th>Offload (lbs)</th>
                    <th>Flow (lbs/min)</th>
                    <th>AR Track</th>
                    <th>Window</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {config.tankers.map((tnk, i) => (
                    <tr key={tnk.id}>
                      <td className="font-mono text-term-primary">{tnk.callsign}</td>
                      <td>{tnk.assetType}</td>
                      <td>
                        <input
                          type="number"
                          value={tnk.totalOffload}
                          onChange={e => updateTanker(i, 'totalOffload', parseInt(e.target.value) || 0)}
                          className="term-input py-1 px-2 w-28 text-xs tabular-nums"
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          value={tnk.flowRate}
                          onChange={e => updateTanker(i, 'flowRate', parseInt(e.target.value) || 0)}
                          className="term-input py-1 px-2 w-20 text-xs tabular-nums"
                        />
                      </td>
                      <td className="text-[10px]">{tnk.arTrack}</td>
                      <td className="text-[10px] tabular-nums">{tnk.availableWindow[0]}–{tnk.availableWindow[1]} min</td>
                      <td>
                        <button
                          onClick={() => deleteTanker(i)}
                          className="text-red-400 hover:text-red-300 text-xs font-bold px-1"
                          title="Remove tanker"
                        >✕</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Receivers Table */}
          <div className="term-box p-3">
            <div className="text-xs text-term-textDim font-medium uppercase tracking-wider mb-2">Receivers</div>
            {config.receivers.length === 0 ? (
              <div className="text-[10px] text-term-textDim italic py-2">No receivers — add one below</div>
            ) : (
              <table className="term-table">
                <thead>
                  <tr>
                    <th>Callsign</th>
                    <th>Type</th>
                    <th>Flight</th>
                    <th>Fuel Cap</th>
                    <th>Current</th>
                    <th>Burn Rate</th>
                    <th>Bingo</th>
                    <th>Priority</th>
                    <th>ARCT</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {config.receivers.map((rcv, i) => (
                    <tr key={rcv.id}>
                      <td className="font-mono text-term-primary">{rcv.callsign}</td>
                      <td className="text-[10px]">{rcv.assetType}</td>
                      <td className="text-xs tabular-nums text-center">×{rcv.flightSize}</td>
                      <td className="tabular-nums text-xs">{rcv.fuelCapacity.toLocaleString()}</td>
                      <td>
                        <input
                          type="number"
                          value={rcv.currentFuel}
                          onChange={e => updateReceiver(i, 'currentFuel', parseInt(e.target.value) || 0)}
                          className="term-input py-1 px-2 w-24 text-xs tabular-nums"
                        />
                      </td>
                      <td className="tabular-nums text-xs">{rcv.burnRate.toLocaleString()}/hr</td>
                      <td className="tabular-nums text-xs">{rcv.bingoFuel.toLocaleString()}</td>
                      <td>
                        <select
                          value={rcv.priority}
                          onChange={e => updateReceiver(i, 'priority', e.target.value)}
                          className="term-input py-1 px-2 text-[10px]"
                        >
                          <option value="HIGH">HIGH</option>
                          <option value="MEDIUM">MEDIUM</option>
                          <option value="LOW">LOW</option>
                        </select>
                      </td>
                      <td className="text-[10px] tabular-nums">{rcv.arctWindow[0]}–{rcv.arctWindow[1]}</td>
                      <td>
                        <button
                          onClick={() => deleteReceiver(i)}
                          className="text-red-400 hover:text-red-300 text-xs font-bold px-1"
                          title="Remove receiver"
                        >✕</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Asset Builder Form */}
          <div className="term-box p-3">
            <div className="text-xs text-term-textDim font-medium uppercase tracking-wider mb-2">Add Asset to Scenario</div>
            <div className="grid grid-cols-4 gap-2">
              {/* Row 1 */}
              <div>
                <label className="text-[9px] text-term-textDim uppercase block mb-0.5">Asset Type</label>
                <select
                  value={builderType}
                  onChange={e => handleBuilderTypeChange(e.target.value)}
                  className="term-input py-1 px-2 text-xs w-full"
                >
                  {ASSET_TYPES.map(t => (
                    <option key={t} value={t}>{t}{ASSET_DEFAULTS[t].role === 'tanker' ? ' (Tanker)' : ''}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[9px] text-term-textDim uppercase block mb-0.5">Callsign</label>
                <input
                  type="text"
                  value={builderCallsign}
                  onChange={e => setBuilderCallsign(e.target.value)}
                  placeholder="RAPTOR 11"
                  className="term-input py-1 px-2 text-xs w-full font-mono"
                />
              </div>
              {!isTankerType && (
                <div>
                  <label className="text-[9px] text-term-textDim uppercase block mb-0.5"># in Flight</label>
                  <select
                    value={builderFlightSize}
                    onChange={e => setBuilderFlightSize(parseInt(e.target.value))}
                    className="term-input py-1 px-2 text-xs w-full"
                  >
                    {[1, 2, 3, 4].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
              )}
              {!isTankerType && (
                <div>
                  <label className="text-[9px] text-term-textDim uppercase block mb-0.5">Priority</label>
                  <select
                    value={builderPriority}
                    onChange={e => setBuilderPriority(e.target.value as ReceiverPriority)}
                    className="term-input py-1 px-2 text-xs w-full"
                  >
                    <option value="HIGH">HIGH</option>
                    <option value="MEDIUM">MEDIUM</option>
                    <option value="LOW">LOW</option>
                  </select>
                </div>
              )}

              {/* Row 2 */}
              {!isTankerType ? (
                <>
                  <div>
                    <label className="text-[9px] text-term-textDim uppercase block mb-0.5">Fuel Req (lbs/ac)</label>
                    <input
                      type="number"
                      value={builderFuelReq}
                      onChange={e => setBuilderFuelReq(parseInt(e.target.value) || 0)}
                      className="term-input py-1 px-2 text-xs w-full tabular-nums"
                    />
                  </div>
                  <div>
                    <label className="text-[9px] text-term-textDim uppercase block mb-0.5">Burn Rate/hr (per ac)</label>
                    <input
                      type="number"
                      value={builderBurnRate}
                      onChange={e => setBuilderBurnRate(parseInt(e.target.value) || 0)}
                      className="term-input py-1 px-2 text-xs w-full tabular-nums"
                    />
                  </div>
                  <div className="flex items-end pb-1">
                    <label className="flex items-center gap-1.5 text-[10px] text-term-textDim cursor-pointer">
                      <input
                        type="checkbox"
                        checked={builderME}
                        onChange={e => setBuilderME(e.target.checked)}
                        className="accent-term-primary"
                      />
                      Mission Essential
                    </label>
                  </div>
                </>
              ) : (
                <div className="col-span-2">
                  <label className="text-[9px] text-term-textDim uppercase block mb-0.5">Starting Offload (lbs)</label>
                  <input
                    type="number"
                    value={builderOffload}
                    onChange={e => setBuilderOffload(parseInt(e.target.value) || 0)}
                    className="term-input py-1 px-2 text-xs w-full tabular-nums"
                  />
                </div>
              )}

              {/* Row 3: ARCT window + Add button */}
              <div>
                <label className="text-[9px] text-term-textDim uppercase block mb-0.5">ARCT Start (Zulu)</label>
                <input
                  type="text"
                  value={builderArctStart}
                  onChange={e => setBuilderArctStart(e.target.value.replace(/[^0-9]/g, '').slice(0, 4))}
                  placeholder="0030"
                  maxLength={4}
                  className="term-input py-1 px-2 text-xs w-full font-mono tabular-nums"
                />
              </div>
              <div>
                <label className="text-[9px] text-term-textDim uppercase block mb-0.5">ARCT End (Zulu)</label>
                <input
                  type="text"
                  value={builderArctEnd}
                  onChange={e => setBuilderArctEnd(e.target.value.replace(/[^0-9]/g, '').slice(0, 4))}
                  placeholder="0100"
                  maxLength={4}
                  className="term-input py-1 px-2 text-xs w-full font-mono tabular-nums"
                />
              </div>
              <div className="col-span-2 flex items-end">
                <button
                  onClick={handleAddAsset}
                  disabled={!builderCallsign.trim()}
                  className="term-button term-button-primary px-4 py-1.5 text-[10px] font-bold uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed w-full"
                >
                  ADD TO SCENARIO
                </button>
              </div>
            </div>
          </div>

          {/* Start Button */}
          <div className="flex justify-end">
            <button
              onClick={handleStart}
              disabled={config.tankers.length === 0 || config.receivers.length === 0}
              className="term-button term-button-primary px-6 py-2 text-sm font-bold uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed"
            >
              START SIMULATION
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
